import { mount, unmount } from 'svelte'
import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import { onConsentReady } from 'frontend/shared/javascripts/util/gdpr_consent'
import safeLocalStorage from 'frontend/shared/javascripts/util/safe_local_storage'
import { inTestEnvironment } from 'shared/javascripts/util/environment'
import { on } from 'shared/javascripts/util/events'
import { inOverlay } from 'shared/javascripts/util/unpoly_compat'
import { trafficSource } from './context'
import { learningIntentForm, type SurveyForm } from './form'

const SHOW_DELAY = inTestEnvironment() ? 0 : 10_000
const RETRY_DELAY = 1_000
// Gives up when something stays in the way; the next full page load tries again.
const MAX_RETRIES = 300

export function surveyDoneKey(form: SurveyForm): string {
  return `survey:${form.id}:done`
}

function surveyDone(form: SurveyForm): boolean {
  return safeLocalStorage().getItem(surveyDoneKey(form)) !== null
}

// Shows the survey once per browser to visitors in variant b of its experiment, after the
// consent banner, the adblock notification, Unpoly overlays and modals like the Studybuddy onboarding.
export function initSurveyBanner(form: SurveyForm = learningIntentForm) {
  trafficSource()
  if (surveyDone(form)) return

  let variant: string | undefined
  let scheduled = false
  let adblockNotificationOpen = false

  on('app.adBlocker.notificationShown', () => (adblockNotificationOpen = true))
  on('app.adBlocker.notificationHidden', () => (adblockNotificationOpen = false))

  const showWhenUnobstructed = (retries = 0): void => {
    // Convert re-evaluates on every navigation, so the visitor may have left the targeted pages.
    if (variant !== 'b') {
      scheduled = false
    } else if (adblockNotificationOpen || inOverlay() || document.querySelector(':modal')) {
      if (retries < MAX_RETRIES) setTimeout(showWhenUnobstructed, RETRY_DELAY, retries + 1)
    } else {
      void showSurveyBanner(form)
    }
  }

  onConsentReady(() => {
    abTests.onVariant(form.experiment, (decided) => {
      variant = decided
      if (decided !== 'b' || scheduled) return
      scheduled = true
      setTimeout(showWhenUnobstructed, SHOW_DELAY)
    })
  })
}

async function showSurveyBanner(form: SurveyForm): Promise<void> {
  const { default: SurveyBanner } = await import('./SurveyBanner.svelte')
  const markDone = () => safeLocalStorage().setItem(surveyDoneKey(form), 'true')

  const target = document.createElement('div')
  document.body.append(target)
  const banner = mount(SurveyBanner, {
    target,
    props: {
      form,
      onSubmit: markDone,
      onClose: () => {
        markDone()
        void unmount(banner)
        target.remove()
      },
    },
  })
}
